
import Vue from 'vue';
import Component from 'vue-class-component';
import { Inject } from '@/core/di/Inject';
import { TYPES } from '@/core/config/Types';
import { Territories } from '@/settings/domain/views/territories/Territories';
import { Entity } from '@/settings/domain/entity/entity/Entity';
import CustomTable from '@/core/components/shared/CustomTable.vue';
import { CommodityType } from '@/freight/domain/commodity_type/CommodityType';
import { Bulk } from '@/wms/domain/bulk/Bulk';
import { SearchTerritoryByQueryDescription } from '@/settings/application/uses_cases/views/territories/SearchTerritoryByQueryDescription';
import { FindCommodityTypeByStatus } from '@/freight/application/commodity_type/search/FindCommodityTypesByStatus';
import { WeightFindAll } from '@/settings/application/uses_cases/weight/search/WeightFindAll';
import { Prop } from 'vue-property-decorator';
import { PreRegister } from '@/wms/domain/preRegister/PreRegister';
import { EntityFindFilter } from '@/settings/application/uses_cases/entity/search/EntityFindFilter';
import { Warehouse } from '@/settings/domain/warehouse/Warehouse';
import { WarehouseFindByStatus } from '@/settings/application/uses_cases/warehouse/search/WarehouseFindByStatus';
import { ConsecutiveFindByWarehouseAndType } from '@/courier/application/uses_cases/consecutive/search/ConsecutiveFindByWarehouseAndType';
import { PreRegisterTypeEnum } from '@/wms/domain/preRegister/PreRegisterTypeEnum';
import { ViewLocationFindAllByWarehouseAndCommodity } from '@/settings/application/uses_cases/views/location/search/ViewLocationFindAllByWarehouseAndCommodity';
import { ViewLocation } from '@/settings/domain/views/location/ViewLocation';
import VueBarcode from 'vue-barcode';
import { PackingUnits } from '@/settings/domain/packing_units/PackingUnits';
import { FindAllPackingUnitsByStatus } from '@/settings/application/uses_cases/packing_units/search/FindPackingUnitsByStatus';
import BillOfLandingAddForm from '../billOfLanding/BillOfLandingAddForm.vue';
import { ShippingMethod } from '@/settings/domain/shippingMethod/ShippingMethod';
import { FindByStatusShippingMethod } from '@/settings/application/uses_cases/shippingMethod/search/FindByStatusShippingMethod';
import { FindByStatusCommodityClass } from '@/settings/application/uses_cases/commodityClass/search/FindByStatusCommodityClass';
import { CommodityClass } from '@/settings/domain/commodityClass/CommodityClass';
import { TypeOperation } from '@/settings/domain/typeOperation/TypeOperation';
import { OperationFindAll } from '@/tracking/application/uses_cases/typeoperation/search/OperationFindAll';
import { BillOfLanding } from '@/wms/domain/billOfLanding/BillOfLanding';
import { CreateBillOfLanding } from '@/wms/application/billOfLanding/create/CreateBillOfLanding';
import { FindallBillOfLanding } from '@/wms/application/billOfLanding/search/FindallBillOfLanding';
import { TransportFindAll } from '@/tracking/application/uses_cases/typetransport/search/findall/TransportFindAll';
import { Transport } from '@/tracking/domain/typetransport/Transport';
import { FindAllTimelineConfigByParams } from '@/tracking/application/uses_cases/timelineConfig/search/findAll/FindAllTimelineConfigByParams';
import { TimelineConfig } from '@/tracking/domain/timelineConfig/TimelineConfig';
import AddCargoDetails from '@/freight/infrastructure/ui/quotation/form/addCargoDetails/AddCargoDetails.vue';
import { Containers } from '@/settings/domain/containers/Containers';
import { FindAllContainersByStatus } from '@/settings/application/uses_cases/containers/search/FindAllContainersByStatus';
import { PackingUnit } from '@/wms/domain/packingUnit/PackingUnit';
import { OrderType } from '@/courier/domain/CourierOrder/OrderTypeEnum';
import { WmsOrderLine } from '@/courier/domain/orderline/WmsOrderLine';
import { WmsOrderFindAllByModule } from '@/courier/application/uses_cases/order/search/WmsOrderFindAllByModule';
import { WmsQuotationOrder } from '@/courier/domain/wmsQuotationOrder/WmsQuotationOrder';
import { WmsOrderFindByPk } from '@/courier/application/uses_cases/order/search/WmsOrderFindByPk';
import AppTimelineComponentFF from '@/tracking/infrastructure/ui/components/ff/timeline/TimelineComponentFF.vue';
import { FindAllTimelineConfigByPk } from '@/tracking/application/uses_cases/timelineConfig/search/findAll/FindAllTimelineConfigByPk';
import { Events } from '@/tracking/domain/events/Events';

interface Lists {
  containerList?: Containers[];
  originList?: Territories[];
  destinationList?: Territories[];
  commodityList?: CommodityType[];
  packagingList?: PackingUnits[];
  referencesList?: any[];
  typeOperationList?: TypeOperation[];
  locationList?: ViewLocation[];
  weightList?: any[];
  commodityClassesList?: CommodityClass[];
  customerList?: Entity[];
  billOfLandingList?: BillOfLanding[];
}

@Component({
  name: 'AddWarehousePreRegister',
  components: { CustomTable, VueBarcode, BillOfLandingAddForm, AddCargoDetails, AppTimelineComponentFF }
})
export default class AddWarehousePreRegister extends Vue {
  @Inject(TYPES.API_VIEW_FIND_TERRITORIES_BY_QUERY)
  readonly searchTerritoriesByQueryParameter!: SearchTerritoryByQueryDescription;
  @Inject(TYPES.COMMODITY_TYPE_FIND_BY_STATUS)
  readonly findActiveCommodity!: FindCommodityTypeByStatus;
  @Inject(TYPES.FINDALL_WEIGHT)
  readonly findAllWeightUnits!: WeightFindAll;
  @Inject(TYPES.ENTITY_FIND_BY_DESCRIPTION_LIKE)
  readonly findEntityByDescription!: EntityFindFilter;
  @Inject(TYPES.FINDBYSTATUS_WAREHOUSE)
  readonly findActiveWarehouses!: WarehouseFindByStatus;
  @Inject(TYPES.FINDBYWAREHOUSEANDTYPE_CONSECUTIVE)
  readonly consecutiveFindByWarehouseAndType!: ConsecutiveFindByWarehouseAndType;
  @Inject(TYPES.VIEW_FIND_LOCATIONS_BY_WAREHOUSE_AND_COMMODITY)
  readonly findLocationsByWarehouseAndCommodity!: ViewLocationFindAllByWarehouseAndCommodity;
  @Inject(TYPES.FIND_PACKING_UNITS_BY_STATUS)
  readonly findActivePackingUnits!: FindAllPackingUnitsByStatus;
  @Inject(TYPES.SHIPPING_METHOD_FIND_BY_STATUS)
  readonly findActiveShippingMethods!: FindByStatusShippingMethod;
  @Inject(TYPES.COMMODITY_CLASS_FIND_BY_STATUS)
  readonly findActiveCommodityClass!: FindByStatusCommodityClass;
  @Inject(TYPES.FINDALL_TYPE_OPERATION)
  readonly findTypeOperations!: OperationFindAll;
  @Inject(TYPES.BILL_OF_LANDING_SAVE)
  readonly saveBillofLanding!: CreateBillOfLanding;
  @Inject(TYPES.BILL_OF_LANDING_FINDALL)
  readonly findAllBillOfLanding!: FindallBillOfLanding;
  @Inject(TYPES.FINDALL_TRANSPORT)
  readonly findTransport!: TransportFindAll;
  @Inject(TYPES.TIMELINECONFIG_TYPE_FINDALL_BY_PARAMS)
  readonly findTimelines!: FindAllTimelineConfigByParams;
  @Inject(TYPES.FIND_CONTAINER_BY_STATUS)
  readonly searchActiveContainers!: FindAllContainersByStatus;
  @Inject(TYPES.QUOTATIONS_FIND_ALL_BY_TYPE_AND_MODULE)
  readonly findApprovedQuotations!: WmsOrderFindAllByModule;
  @Inject(TYPES.WMS_ORDER_FIND_BY_PK)
  readonly findWmsOrderByPk!: WmsOrderFindByPk;
  @Inject(TYPES.TIMELINECONFIG_TYPE_FINDALL_BY_PK)
  readonly findTimelineConfigByPk!: FindAllTimelineConfigByPk;

  //Listas
  originList: Territories[] = [];
  destinationList: Territories[] = [];
  customerList: Entity[] = [];
  commodityList: CommodityType[] = [];
  weightList: any[] = [];
  warehouseList: Warehouse[] = [];
  locationList: ViewLocation[] = [];
  packingList: PackingUnits[] = [];
  shippingMethodList: ShippingMethod[] = [];
  commodityClassesList: CommodityClass[] = [];
  typeOperationList: TypeOperation[] = [];
  billOfLandingList: BillOfLanding[] = [];
  typeTransportList: Transport[] = [];
  timelinesList: TimelineConfig[] = [];
  quotationsList: WmsQuotationOrder[] = [];

  //Listas
  lists: Lists = {
    containerList: [],
    originList: [],
    destinationList: [],
    commodityList: [],
    packagingList: [],
    referencesList: [],
    typeOperationList: [],
    locationList: [],
    weightList: [],
    commodityClassesList: [],
    billOfLandingList: [],
    customerList: []
  };

  //Otra data
  eventList: Events[] = [];
  isLoading = false;
  bulkQuantity = 1;
  openTags = true;
  bulkForBarcodeModal: {
    index: 0;
    bulk: Bulk | null;
  } = {
    index: 0,
    bulk: null
  };

  //Forms items
  forms = {
    newBulk: new Bulk(),
    newContainer: new Bulk(),
    newBox: new Bulk()
  };

  //Form BL
  formBL: BillOfLanding = new BillOfLanding();

  //Form helpers
  senderAddress = '';
  quotationForLoad: WmsQuotationOrder | null = null;

  //PROPS
  @Prop({ required: true }) form!: PreRegister;
  @Prop({ required: true }) componentType!: number;
  @Prop({ required: false }) actions!: any;
  @Prop({ required: false }) forView!: boolean;
  @Prop({ required: false }) module!: string;

  //Objeto de acciones en la tabla
  tableActions = {
    delete: this.deleteBulks,
    edit: this.loadBulk,
    customActions: [
      {
        title: `${this.$t('general.barCode')}`,
        action: this.showBarcode,
        icon: 'fa fa-barcode'
      }
    ]
  };

  //Referencias de objetos
  $refs!: {
    barCodeModal: HTMLFormElement;
    blCreation: HTMLFormElement;
  };

  //VARIABLES PARA INTERVALO DE BUSQUEDA
  timeoutOrigin: any;
  timeoutDestination: any;
  timeoutEntity: any;
  searchInterval = 600;

  //Objeto de acciones para modal de crear BL
  actionsModal = {
    save: this.saveBL,
    clear: this.clearBl
  };

  //Ciclo de vida del componente
  mounted() {
    this.getAllData();
  }

  //Funcion invocada para obtener la data necesaria en las listas
  getAllData() {
    this.getCommodities();
    this.getWeihgtUnits();
    this.findWarehouses();
    this.findPackingUnits();
    this.findShippingMethods();
    this.findCommodityClasses();
    this.getTypeOperation();
    this.findAllTransport();
    this.getContainers();
    this.getPackingUnits();
    this.findQuotations();
  }

  //Getter para obtener los campos a mostra en la tabla
  get fields() {
    return [
      {
        label: `#`,
        key: 'number',
        class: 'col-1'
      },
      {
        label: `Serial`,
        key: 'serial',
        formatter: (item: number) => (!item ? 'No generado' : item)
      },
      {
        label: `${this.$t('general.description')}`,
        key: 'description',
        formatter: (item: string, index: number, row: Bulk) =>
          item && item.length > 0
            ? item
            : row.shippingDetails && row.shippingDetails.productName && row.shippingDetails.productName.length
            ? row.shippingDetails.productName
            : '-'
      },
      {
        label: `${this.$t('general.commodity')}`,
        key: 'commodity',
        formatter: (item: CommodityType, index: number, row: Bulk) =>
          item ? item.description : row.shippingDetails.commodity ? row.shippingDetails.commodity.description : `-`
      },
      this.form.typeTransport && this.form.typeTransport.name == 'ground'
        ? {
            label: `${this.$t('general.class')}`,
            key: 'commodityClass',
            formatter: (item: CommodityClass) => (item ? item.description : `-`)
          }
        : null,
      {
        label: `${this.$t('general.location')}`,
        key: 'location',
        formatter: (item: ViewLocation) => (item ? item.descriptionLocation : `-`)
      },
      {
        label: `${this.$t('general.dimentions')}`,
        key: 'dimentions',
        formatter: (item: number, index: number, row: Bulk) => `${row.length}x${row.width}x${row.height}`
      },
      {
        label: `${this.$t('general.weight')}`,
        key: 'weight'
      },
      {
        label: `${this.$t('general.weightUnit')}`,
        key: 'weightUnit',
        formatter: (item: any) => (item ? item.description : `-`)
      },
      {
        label: `${this.$t('general.packaging')}`,
        key: 'packingUnit.description',
        formatter: (item: PackingUnit) => (item ? item.description : `-`)
      },
      {
        label: `${this.$t('general.type')}`,
        key: 'quotationTypeLine'
      },
      this.showFormBulks && !this.forView
        ? {
            label: `${this.$t('general.actions')}`,
            key: 'actions',
            class: 'col-4'
          }
        : null
    ];
  }

  //Getter para obtener los parametros de la URL
  get urlParams() {
    return {
      query: this.$route.query
    };
  }

  get processTypeSelect() {
    if (this.form.typeOperationData == null) return 0;
    return this.form.typeOperationData.processType;
  }

  set processTypeSelect(newValue: number) {
    this.form.typeOperation = newValue;
  }

  get partialPreRegisterState() {
    return this.form.state.includes('partial');
  }

  //Getter para obtener una decision de mostrar o no el form de bultos.
  get showFormBulks() {
    return !this.form.type.includes('WR');
  }

  //Getter para obtener los tipos de operaciones y si descripcion
  get typeOperationOptions() {
    return [
      {
        value: 1,
        html: `<strong>${this.$t('general.importation')}</strong><p>${this.$t('quotation.importationDescription')}</p>`
      },
      {
        value: 2,
        html: `<strong>${this.$t('general.exportation')}</strong><p>${this.$t('quotation.importationDescription')}</p>`
      },
      {
        value: 3,
        html: `<strong>${this.$t('general.localOperation')}</strong><p>${this.$t(
          'quotation.importationDescription'
        )}</p>`
      }
    ];
  }

  //Getter para obtener un booleano para deshabilitar los inputs
  get isDisabled() {
    return this.componentType != 1 || this.forView;
  }

  //Getter para obtener el telefono principal del cliente
  get mainContactPhone() {
    if (this.form.sender == null || !('contacts' in this.form.sender)) return '';
    const mainContact = this.form.sender.contacts.filter(item => item.mainContact);
    return mainContact.length > 0 ? mainContact[0].phone : '';
  }

  //Getter para obtener la cantidad de grupos de bultos que existen en la lista
  get bulkGroupNumber() {
    if (this.form.bulks.length < 1) return 0;
    return Math.max(...this.form.bulks.map(bulk => bulk.group));
  }

  //Getter para obtener la user data desde localstorage
  get userData() {
    return `${localStorage.getItem('customerId') as string} - ${localStorage.getItem('userNameCompany') as string}`;
  }

  //Funcion invocada para agregar un bulto
  addBulk(item: Bulk, quantity: number, form: string) {
    const newBulk = item;
    newBulk.quotationTypeLine = form;
    newBulk.group = this.bulkGroupNumber + 1;
    const bulksNumber = this.form.bulks.length + 1;

    for (let index = 0; index < quantity; index++) {
      const newBulkCopy = Object.assign({}, item);
      newBulkCopy.number = bulksNumber + index;
      this.form.bulks.push(newBulkCopy);
    }

    this.clearBulkForm(form);
  }

  //Funcion invocada para eliminar un grupo de bultos
  deleteBulks(item: any) {
    this.form.bulks = this.form.bulks.filter(bulk => {
      return item.group != bulk.group;
    });
    this.reorderBulks();
  }

  //Funcion invocada para reordenar numeros de bultos
  reorderBulks() {
    this.form.bulks.forEach((item, index) => (item.number = index + 1));
  }

  //Funcion invocada para editar un bulto de la lista
  loadBulk(item: Bulk) {
    switch (item.quotationTypeLine) {
      case 'container':
        this.forms.newContainer = Object.assign(new Bulk(), item);
        break;
      case 'box':
        this.forms.newBox = Object.assign(new Bulk(), item);
        break;
      case 'bulk':
        this.forms.newBulk = Object.assign(new Bulk(), item);
        break;
      default:
        return;
    }
    this.deleteBulks(item);
    this.$toasted.show(
      `${this.$t('general.loadedToSection', { section: this.$t(`general.${item.quotationTypeLine}`) })}`
    );
  }

  //Funcion invocada para limpiar el formulario de adicionar bulto
  clearBulkForm(form?: string) {
    switch (form) {
      case 'container':
        this.forms.newContainer = new Bulk();
        break;
      case 'bulk':
        this.forms.newBulk = new Bulk();
        break;
      case 'box':
        this.forms.newBox = new Bulk();
        break;
      default:
        this.forms = {
          newBulk: new Bulk(),
          newContainer: new Bulk(),
          newBox: new Bulk()
        };
        break;
    }
  }

  // Este método es llamado cuando el usuario escribe sobre el input de origen o destino para buscar territorios.
  findTerritoriesFactory(query: string, input: string, delayed = true): void {
    //Limpiamos cualquier función anterior en el timeout para evitar búsquedas repetitivas y solapadas.
    delayed ? clearTimeout(this.timeoutOrigin) : null;
    this.timeoutOrigin = setTimeout(
      async () => {
        if (query.length >= 3) {
          try {
            const res = await this.findTerritories(query);
            input == 'origin' ? (this.originList = res) : (this.destinationList = res);
          } catch (error) {
            this.isLoading = false;
            throw new Error(`${error}`);
          }
        }
        clearTimeout(this.timeoutOrigin);
      },
      delayed ? this.searchInterval : 0
    );
  }

  //Metodo general de busqueda de territorios
  async findTerritories(query: string): Promise<Territories[]> {
    this.isLoading = true;
    const res = await this.searchTerritoriesByQueryParameter.execute(query.toUpperCase());
    this.isLoading = false;
    return res;
  }

  //Funcion invocada para buscar entidades basadas en el nombre
  findEntity(query: string) {
    try {
      clearTimeout(this.timeoutEntity);
      if (query.length < 2) return;
      const params: any = {
        text: query,
        type: ['SUPPLIER', 'CUSTOMER']
      };
      this.timeoutEntity = setTimeout(async () => {
        this.isLoading = true;
        const res = await this.findEntityByDescription.execute(params);
        this.customerList = res.length > 0 ? res : [];
        this.isLoading = false;
        clearTimeout(this.timeoutEntity);
      }, this.searchInterval);
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para hacer set de la direccion del remitente
  async setAddress(input: Entity) {
    this.form.address.description = `${input.address.description}`;
    this.form.address.zipCode = input.address.zipCode;
    const res = await this.findTerritories(input.address.cityName);
    this.form.address.territory = res.filter(item => item.city == input.address.city)[0];
  }

  //Funcion invocada para obtener todos los almacenes activos
  async findWarehouses() {
    try {
      this.isLoading = true;
      const res = await this.findActiveWarehouses.execute(true);
      this.warehouseList = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para obtener el consecutivo del documento al seleccionar un almacen
  async getConsecutive(input: Warehouse) {
    try {
      this.isLoading = true;
      const res: {
        createAt: string;
        nextNumber: string;
        type: string;
        updateAt: string;
      } = (await this.consecutiveFindByWarehouseAndType.execute({
        type: `${input.code}_${PreRegisterTypeEnum.PRE_REGISTER}`,
        warehouse: input.code
      })) as any;
      this.form.number = res.nextNumber + 1;
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para obtener la lista de localizaciones de un almacen, filtrando por commodity
  async findLocations(input: CommodityType) {
    try {
      this.isLoading = true;
      const res = await this.findLocationsByWarehouseAndCommodity.execute({
        warehouseCode: this.form.warehouse?.code,
        commodityId: input.id
      });
      this.locationList = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para asignar un bulto al objeto bulkForBarcodeModal y mostrar el modal
  showBarcode(bulk: Bulk) {
    this.bulkForBarcodeModal.bulk = Object.assign({}, bulk);
    this.$refs.barCodeModal.show();
  }

  //Funcion invocada para validar
  handdleOk() {
    return;
  }

  //Funcion invocada para obtener la lista de Embalajes
  async findPackingUnits() {
    try {
      this.isLoading = true;
      const res = await this.findActivePackingUnits.execute(true);
      this.packingList = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para obtener la lista de modalidades de envio
  async findShippingMethods() {
    try {
      this.isLoading = true;
      const res = await this.findActiveShippingMethods.execute(true);
      this.shippingMethodList = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar los tipos de operaciones y cargar el maestro
  async getTypeOperation() {
    try {
      this.isLoading = true;
      const res = await this.findTypeOperations.execute();
      this.typeOperationList = res.length > 0 ? (res as any) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para guardar un BL en la base de datos
  async saveBL() {
    try {
      this.isLoading = true;
      await this.saveBillofLanding.execute(this.formBL);
      this.findAllBL();
      this.clearBl();
      this.$refs.blCreation.hide();
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para limpiar el formulario de BL
  clearBl() {
    this.formBL = new BillOfLanding();
  }

  //Funcion invocada para obtener todos los metodos de transporte
  async findAllTransport() {
    try {
      this.isLoading = true;
      const res = await this.findTransport.execute();
      this.typeTransportList = res.length > 0 ? res.reverse() : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para validar un input
  getValidationState({ dirty, validated, valid = null }: { dirty: any; validated: any; valid: any }) {
    return dirty || validated ? valid : null;
  }

  //Funcion invocada para obtener los timeline que aplican al tipo de registro que se está realizando
  async getTimelines(input?: Transport) {
    try {
      this.isLoading = true;
      const res = await this.findTimelines.execute({
        configFor: this.module === 'wms' ? 'w' : 'c',
        typeOperation: this.form.typeOperationData?.code,
        typeTransport: input?.id || this.form.typeTransport?.id
      });
      this.timelinesList = res.length > 0 ? res.reverse() : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar containers activos
  async getContainers() {
    try {
      this.isLoading = true;
      const res = await this.searchActiveContainers.execute(true);
      res.length > 0 ? (this.lists.containerList = res) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar los commodity activos
  async getWeihgtUnits() {
    try {
      this.isLoading = true;
      const res = await this.findAllWeightUnits.execute();
      this.lists.weightList = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }
  }

  //Funcion invocada para buscar los commodity activos
  async getCommodities() {
    try {
      this.isLoading = true;
      const res = await this.findActiveCommodity.execute(true);
      this.lists.commodityList = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
    }
  }

  //Funcion invocada para obtener todos los BL creados
  async findAllBL() {
    try {
      this.isLoading = true;
      const res = await this.findAllBillOfLanding.execute();
      this.lists.billOfLandingList = res.length > 0 ? res.reverse() : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar todos las unidades de peso
  async getPackingUnits() {
    try {
      this.isLoading = true;
      const res = await this.findActivePackingUnits.execute(true);
      res.length > 0 ? (this.lists.packagingList = res) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para obtener la lista de clases de commodity de envio
  async findCommodityClasses() {
    try {
      this.isLoading = true;
      const res = await this.findActiveCommodityClass.execute(true);
      this.lists.commodityClassesList = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para obtener todas las cotizaciones (Deberian ser las cotizaciones aprobadas)
  async findQuotations() {
    try {
      this.isLoading = true;
      const res = await this.findApprovedQuotations.execute({ orderType: OrderType.freightRequest, module: 'carga_f' });
      this.quotationsList = res.length > 0 ? res.reverse() : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para obtener una cotizacion a partir del tipo y numero de esta misma
  async findQuotationByPk() {
    try {
      if (!this.quotationForLoad) return;
      this.isLoading = true;
      const res = await this.findWmsOrderByPk.execute({
        number: this.quotationForLoad.number,
        type: this.quotationForLoad.typeOrder
      });
      if (!('error' in res)) {
        this.form.bulks = this.form.bulks.concat(this.form.bulks, this.convertLinesToBulks(res.lines));
        this.quotationForLoad = null;
      }
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para convertir los lines de una orden a bultos
  convertLinesToBulks(lines: WmsOrderLine[]): Bulk[] {
    return lines
      .filter(line => line.quotationTypeLine != 'additional')
      .map(line => {
        let newBulk = new Bulk();
        newBulk = Object.assign(newBulk, line);
        return newBulk;
      });
  }

  //Funcion invocada para ejecutar un save y luego hacer un fetch de la data que alimenta las listas
  save() {
    this.actions.save();
    this.getAllData();
  }

  //Funcion para obtener los eventos del timeline seleccionado
  async getTimelineDetails(input: TimelineConfig) {
    try {
      this.isLoading = true;
      const res = await this.findTimelineConfigByPk.execute(input.id);
      this.eventList = 'id' in res ? res.events : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }
}
